import { useEffect, useState } from 'react'

/**
 * Is the on-screen keyboard open?
 *
 * ## Why this exists
 *
 * The viewport meta carries `interactive-widget=resizes-content`, so when the
 * keyboard opens the content box shrinks and anything `position: fixed;
 * bottom: 0` re-pins directly ON TOP of the keyboard. The bottom tab bar then
 * sits in the strip between the text field and the keys — which no native app
 * does, and which costs 64px of the little room left for the thing being typed
 * into. Every native tab bar hides while a field is focused and comes back when
 * it blurs.
 *
 * ## Why `visualViewport` and not `focusin`
 *
 * Focus is the wrong signal. A `<select>`, a date picker and a read-only field
 * all take focus without opening a keyboard, and on a tablet with a hardware
 * keyboard attached nothing opens at all — so a focus-driven bar would vanish
 * for people who never saw a keyboard. `visualViewport.height` is the effect
 * itself rather than a proxy for it.
 *
 * The 120px threshold is deliberately coarse: iOS Safari also shrinks the
 * visual viewport by ~40–90px when the URL bar expands on scroll-up, and
 * treating that as a keyboard would make the tab bar flicker while scrolling.
 * No real on-screen keyboard is under 120px.
 *
 * ## Fails safe
 *
 * No `visualViewport` (older Safari, some webviews) → always reports `false`,
 * so the bar simply behaves as it does today. A bar that stays is a minor
 * annoyance; a bar that disappears and never returns is a trap.
 */
export function useOnScreenKeyboard(): boolean {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    /* No requestAnimationFrame here, deliberately. The first version wrapped
       the measurement in one, which buys nothing — `visualViewport` fires at a
       sane rate on its own — and costs a frame of latency plus a real failure
       mode: rAF is throttled to a stop while the tab is backgrounded, so the
       bar could be left in the wrong state. Found while testing this: the
       synthetic resize produced hiddenPx 300 and the bar never moved. */
    const measure = () => {
      // How much of the layout viewport the visual viewport has lost.
      const hidden = window.innerHeight - vv.height - vv.offsetTop
      setOpen(hidden > 120)
    }

    measure()
    vv.addEventListener('resize', measure)
    vv.addEventListener('scroll', measure)
    return () => {
      vv.removeEventListener('resize', measure)
      vv.removeEventListener('scroll', measure)
    }
  }, [])

  return open
}
